import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch, useFormState } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { ReactComponent as ChevronDown } from 'assets/icons/chevron-down.svg';
import { ReactComponent as ChevronUp } from 'assets/icons/chevron-up.svg';

import PhoneInput from 'components/PhoneInput';
import Input from 'components/Input';
import CheckoutButton from './CheckoutButton';

import schema, { FIELD_NAMES } from './ShopperDetailsForm.schema';
import { shopperDetails as TEXT } from 'locales/en/cart';
import {
  addShopperDetails,
  setCheckoutStep,
  setRenderStripeForm,
  setShippingAddressDisabledState
} from 'redux/slices/checkout';
import { saveShopperDetails } from 'redux/slices/checkoutForm';
import { getStoreColors } from '../helpers';

const ShopperDetailsForm = ({ storeId }) => {
  const dispatch = useDispatch();
  const [isExpanded, setIsExpanded] = useState(true);
  const shopperDetails = useSelector(
    (state) => state.checkoutForm.shopperDetails
  );
  /* https://github.com/react-hook-form/react-hook-form/discussions/3715
  temp fix with pin version to 7.3.4 */
  const defaultValues = Object.assign(
    {},
    {
      name: shopperDetails.name,
      email: shopperDetails.email,
      phone: shopperDetails.phone,
      phone_country: Object.assign({}, { ...shopperDetails.phone_country }),
    }
  );
  const { register, handleSubmit, control } = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });
  const form = useWatch({
    control,
  });
  const { errors, isSubmitting } = useFormState({ control });

  useEffect(() => {
    dispatch(setShippingAddressDisabledState(false));
    dispatch(setRenderStripeForm(false));
  }, [dispatch]);

  const onSubmit = (data) => {
    const { phone_country, phone, name, email } = data;
    const _name = name.split(' ');
    dispatch(
      addShopperDetails({
        email,
        name: _name[0],
        surname: _name[1],
        phone_number: `${phone_country.dial_code}${phone}`,
        store_id: storeId,
      })
    );
    return dispatch(setCheckoutStep(4));
  };

  const onClickExpand = () => {
    setIsExpanded(!isExpanded);
  };

  const onBlur = (e) => {
    dispatch(saveShopperDetails(form));
  };

  return (
    <>
      <div
        className="flex justify-between items-center"
        onClick={onClickExpand}
      >
        <h2
          data-testid="page-title"
          className="text-javelin-gray-dark text-sm lg:font-bold lg:text-2xl mb-1"
        >
          {TEXT.title}
        </h2>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer lg:hidden" />
        ) : (
          <ChevronDown className="w-4 h-4 my-auto text-javelin-blue-dark fill-current cursor-pointer lg:hidden" />
        )}
      </div>
      <div
        className={`lg:block lg:h-full overflow-hidden lg:overflow-visible transition-height ease-in-out ${isExpanded ? 'h-128' : 'h-0'
          }`}
      >
        <h3 className="text-javelin-gray-dark text-opacity-30 text-sm lg:text-lg mb-5">
          {TEXT.subtitle}
        </h3>
        <form
          noValidate
          onSubmit={handleSubmit(onSubmit)}
          onBlur={onBlur}
          className={`flex flex-col justify-between overflow-x-visible ${getStoreColors()}`}
        >
          <div data-testid="form-fields" className="mx-0.5 lg:mx-0">
            <Input
              label={TEXT.name}
              name={FIELD_NAMES.name}
              register={register}
              required
              error={errors.name?.message}
            />
            <Input
              label={TEXT.email}
              name={FIELD_NAMES.email}
              type="email"
              register={register}
              required
              error={errors.email?.message}
            />
            <PhoneInput
              name={FIELD_NAMES.phone}
              control={control}
              countryFieldName={FIELD_NAMES.phone_country}
              label={TEXT.phone}
              error={errors.phone?.message}
              defaultCountry={defaultValues.phone_country}
              defaultPhoneNumber={defaultValues.phone}
            />
          </div>
          <CheckoutButton title={TEXT.submit} showSpinner={isSubmitting} className="btn-blue p-4 text-base lg:max-w-5xl w-full my-9" />
        </form>
      </div>
    </>
  );
};

export default ShopperDetailsForm;
